import { ErrorBoundary } from '@sentry/react';
import { Tooltip, Typography } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { teamLinkAtom } from '@/atoms/navigation';
import { unwrappedRawTeamsAtom, unwrappedTeamIdMappingAtom } from '@/atoms/queries/teams';
import { unwrappedUserNavOverridesAtom } from '@/atoms/queries/user';
import { ClassicEntityLink, type TypographyVariant } from '@/components/navigation/ClassicEntityLink';
import useGetLinkPath from '@/hooks/useGetLinkPath';
import { LinkObject } from '@/types/generic';
import { classicEntityDashboardUrl } from '@/utils/classicLinks';

export type TeamLinkProps = LinkObject & {
  variant?: TypographyVariant;
};

const NewTeamLink = ({
  id,
  label,
  variant,
}: {
  id: LinkObject['id'];
  label: LinkObject['label'];
  variant: TypographyVariant;
}) => {
  const { to, relative } = useGetLinkPath(teamLinkAtom, { teamId: id });
  return (
    <Typography as={RouterLink} variant={variant} to={to} relative={relative}>
      {label}
    </Typography>
  );
};

/*
 * Provides a link to a team dashboard.
 * Supports both classic and new team experience.
 */
export const TeamLink = ({ id, label, variant = 'linkRegular' }: TeamLinkProps) => {
  const { t } = useTranslation('navigation');
  const isOldTeamXp = useAtomValue(unwrappedUserNavOverridesAtom).team;
  const teamsMapping = useAtomValue(unwrappedTeamIdMappingAtom);
  const teams = useAtomValue(unwrappedRawTeamsAtom);
  const iqId = teamsMapping[id];
  const classicTeamHref = classicEntityDashboardUrl('team', iqId);

  const linkComponent = isOldTeamXp ? (
    <ClassicEntityLink href={classicTeamHref} variant={variant}>
      {label}
    </ClassicEntityLink>
  ) : (
    <ErrorBoundary fallback={<Typography variant={variant}>{label}</Typography>}>
      <NewTeamLink id={id} label={label} variant={variant} />
    </ErrorBoundary>
  );

  return teams.find(team => team.team_id === id) ? (
    linkComponent
  ) : (
    <Tooltip content={t('teamNotAvailable')}>{label}</Tooltip>
  );
};
